import * as Sentry from "@sentry/react";

import type { AppConfig } from "../../app-config";

import { getSentryEnvironment, getSentryRelease, isSentryEnabled } from "./SentryService.utils";

type Tags = Record<string, string | number | boolean>;

const init = (): void => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.init({
    environment: getSentryEnvironment(),
    // Without a release, "Resolve in the next release" is unavailable and a plain
    // Resolve is reopened by the very next event - including one from a stale bundle
    // still loaded in a long-lived WebView session. `REACT_APP_VERSION` is the
    // package version, which semantic-release bumps per release.
    release: getSentryRelease(),
    dsn: "https://6d7dd362823029ab9f73c74cfbdb6785@o1324044.ingest.us.sentry.io/4508952646778881",
    skipBrowserExtensionCheck: true,
  });
};

/**
 * Identify the document behind every event the widget sends from now on. Called as soon
 * as the native app's config is accepted - before the first render - so even a report
 * from the first commit carries it. Identifiers only: never the document body.
 */
const setWidgetContext = (config: AppConfig): void => {
  if (!isSentryEnabled()) {
    return;
  }

  if (config.accountSlug) {
    Sentry.setUser({ id: config.accountSlug });
  }

  const tags: Record<string, string | null> = {
    account: config.accountSlug,
    application: config.applicationId,
    record: config.recordId,
    report: config.reportId,
    field: config.fieldSlug,
    preset: config.preset,
    platform: config.platform,
    editable: String(config.editable),
    ss_env: config.env,
  };

  Sentry.setTags(Object.fromEntries(Object.entries(tags).filter(([, value]) => value !== null)));
};

const addBreadcrumb = (breadcrumb: Sentry.Breadcrumb): void => {
  if (!isSentryEnabled()) {
    return;
  }

  Sentry.addBreadcrumb(breadcrumb);
};

const captureException = ({
  error,
  data,
  level,
  tags,
  fingerprint,
}: {
  error: Error;
  data?: unknown;
  level?: Sentry.SeverityLevel;
  /** Indexed by Sentry, so events can be filtered and grouped by them - unlike `data`. */
  tags?: Tags;
  /** Overrides stack-trace grouping, which lumps every call site report into one issue. */
  fingerprint?: string[];
}): void => {
  if (!isSentryEnabled()) {
    return;
  }

  const scope = new Sentry.Scope();

  if (data) {
    scope.setExtra("data", data);
  }

  if (level) {
    scope.setLevel(level);
  }

  if (tags) {
    scope.setTags(tags);
  }

  if (fingerprint) {
    scope.setFingerprint(fingerprint);
  }

  Sentry.captureException(error, scope);
};

export const SentryService = {
  init,
  setWidgetContext,
  addBreadcrumb,
  captureException,
};
